/**
 * Uploads a single file to S3 using a presigned URL.
 *
 * KEPT AS-IS, UNUSED BY THE CURRENT FLOW: retained only for the legacy
 * submit-then-upload-in-background flow (SubmitVisit/CompleteVisitUpload)
 * via uploadMultipleToS3, until that flow is fully retired.
 */
export async function uploadToS3(file, presignedUrl, timeoutMs = 30000, externalController = null) {
    const controller = externalController || new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
        const response = await fetch(presignedUrl, {
            method: 'PUT',
            body: file,
            headers: {
                'Content-Type': file.type
            },
            signal: controller.signal
        });

        if (!response.ok) {
            throw new Error(`Upload failed: ${response.status}`);
        }
    } finally {
        clearTimeout(timeoutId);
    }
}

/**
 * Uploads multiple files to S3 one at a time (not in parallel).
 *
 * Kept as-is for the legacy submit-then-upload-in-background flow
 * (SubmitVisit/CompleteVisitUpload) until that flow is retired. The new
 * per-file-immediate-upload flow (useVisitMediaUpload) does not use this —
 * see uploadMultipart below.
 *
 * @param {Array<{file: File, presignedUrl: string, type: string, key: string, visibility: string}>} uploads
 * @param {(current: number, total: number) => void} [onProgress] - called right as each file BEGINS (1-indexed)
 * @returns {Promise<{succeeded: Array, failed: Array}>}
 */
export async function uploadMultipleToS3(uploads, onProgress) {
    const total = uploads.length;
    const succeeded = [];
    const failed = [];

    for (let i = 0; i < uploads.length; i++) {
        const { file, presignedUrl, type, key, visibility } = uploads[i];

        if (onProgress) onProgress(i + 1, total);

        try {
            await uploadToS3(file, presignedUrl);
            succeeded.push({ type, key, visibility });
        } catch (error) {
            failed.push(error);
        }
    }

    return { succeeded, failed };
}

// ═══════════════════════════════════════════════════════════════════════
// 1E.3: Multipart upload — always used now (small files just resolve to a
// single part). Functional core (pure, exported for testing) / imperative
// shell (XHR calls) split, same discipline as the backend's SOP §4.38.
// ═══════════════════════════════════════════════════════════════════════

// Must match AddVisitMedia's CHUNK_SIZE_BYTES exactly.
export const CHUNK_SIZE_BYTES = 5 * 1024 * 1024;

// 1E.3a decision: 5 attempts per chunk, 4 backoff delays between them.
export const PART_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000];

// 1E.3 stall detection, rebuilt on real byte-level progress (previously a
// fixed whole-part ceiling using fetch(), which can't see bytes in flight —
// on a slow-but-genuinely-transferring connection, that fixed ceiling
// would kill and restart parts that were never actually stalled, wasting
// real data and occasionally never succeeding within the retry budget at
// all). This timer resets on every byte of confirmed progress — only true
// inactivity trips it.
export const PART_INACTIVITY_TIMEOUT_MS = 30000;

// Hard backstop, NOT reset by progress — purely a last-resort guard against
// a truly pathological case (a part that keeps trickling *some* bytes but
// never finishes) hanging forever. Not expected to ever fire in practice.
export const PART_ABSOLUTE_MAX_MS = 20 * 60 * 1000;

// ── Pure logic ────────────────────────────────────────────────────────

export function computeByteRange(partNumber, fileSize, chunkSize = CHUNK_SIZE_BYTES) {
    const start = (partNumber - 1) * chunkSize;
    const end = Math.min(start + chunkSize, fileSize);
    return { start, end };
}

export function partByteLength(partNumber, fileSize, chunkSize = CHUNK_SIZE_BYTES) {
    const { start, end } = computeByteRange(partNumber, fileSize, chunkSize);
    return end - start;
}

export function estimatePartCount(fileSize, chunkSize = CHUNK_SIZE_BYTES) {
    if (!Number.isFinite(fileSize) || fileSize <= 0) return 1;
    return Math.max(1, Math.ceil(fileSize / chunkSize));
}

// Combines every part's latest known byte count (in-flight or completed)
// into a single 0-100 percentage of the whole file. bytesByPart values are
// always the LATEST known loaded count for that part, not deltas — a
// retried part naturally resets its own entry to 0 on restart, which
// correctly (and honestly) dips the combined total.
export function computeCombinedBytesPct(bytesByPart, fileSize) {
    if (!fileSize || fileSize <= 0) return 0;
    const totalLoaded = Object.values(bytesByPart).reduce((sum, b) => sum + b, 0);
    return Math.max(0, Math.min(100, Math.round((totalLoaded / fileSize) * 100)));
}

export async function withRetry(fn, delays = PART_RETRY_DELAYS_MS, sleep = defaultSleep) {
    let lastError;
    const totalAttempts = delays.length + 1;

    for (let attempt = 1; attempt <= totalAttempts; attempt++) {
        try {
            return await fn(attempt);
        } catch (error) {
            // Two error types skip retry entirely, for different reasons:
            // AbortError (an explicit user cancel — retrying would be
            // wrong), and PresignedUrlExpiredError (a dead URL that will
            // fail identically on every attempt — retrying would just
            // waste the backoff delay before failing anyway).
            if (error?.name === 'AbortError' || error?.name === 'PresignedUrlExpiredError') throw error;
            lastError = error;
            if (attempt <= delays.length) {
                await sleep(delays[attempt - 1]);
            }
        }
    }
    throw lastError;
}

// A minimal, dependency-free bounded-concurrency task queue. A single
// shared instance per upload session (created once in useVisitMediaUpload,
// held in a ref) — that's what makes the concurrency cap GLOBAL across
// every file's chunks in the session, not per-file.
export function createConcurrencyPool(limit) {
    let active = 0;
    const queue = [];

    function runNext() {
        if (active >= limit || queue.length === 0) return;
        active++;
        const { fn, resolve, reject } = queue.shift();
        Promise.resolve()
            .then(fn)
            .then(resolve, reject)
            .finally(() => {
                active--;
                runNext();
            });
    }

    return {
        run(fn) {
            return new Promise((resolve, reject) => {
                queue.push({ fn, resolve, reject });
                runNext();
            });
        },
        get active() {
            return active;
        },
        get pending() {
            return queue.length;
        },
    };
}

// ── Impure shell (XHR calls) ─────────────────────────────────────────

function defaultSleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

function abortableSleep(ms, signal) {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(Object.assign(new Error('Aborted'), { name: 'AbortError' }));
            return;
        }
        const timeoutId = setTimeout(resolve, ms);
        signal?.addEventListener(
            'abort',
            () => {
                clearTimeout(timeoutId);
                reject(Object.assign(new Error('Aborted'), { name: 'AbortError' }));
            },
            { once: true }
        );
    });
}

/**
 * Uploads exactly one part via XMLHttpRequest (not fetch) specifically for
 * its upload.onprogress event — real byte-level visibility fetch cannot
 * provide. Two independent timers protect against a hang:
 *
 * - Inactivity timer (inactivityTimeoutMs): reset on every progress event.
 *   Only fires if NO new bytes have been confirmed for the whole window —
 *   a genuinely slow-but-transferring part never trips this, no matter how
 *   long it takes overall.
 * - Absolute-max timer (absoluteMaxMs): NOT reset by progress. A last-
 *   resort backstop for a pathological case that keeps trickling bytes
 *   without ever finishing.
 *
 * Both are distinguished from an explicit external cancel (the caller's
 * AbortController) via the `externallyAborted` flag, so only a real user
 * cancel propagates as AbortError (skips retry) — a stall or absolute-max
 * trip is a normal, retryable failure.
 */
function uploadPartXHR(file, partNumber, uploadUrl, controller, { inactivityTimeoutMs, absoluteMaxMs, onProgress }) {
    return new Promise((resolve, reject) => {
        if (controller.signal.aborted) {
            reject(Object.assign(new Error('Aborted'), { name: 'AbortError' }));
            return;
        }

        const { start, end } = computeByteRange(partNumber, file.size);
        const blob = file.slice(start, end);

        const xhr = new XMLHttpRequest();
        let settled = false;
        let externallyAborted = false;
        let inactivityTimer = null;
        let absoluteTimer = null;

        function clearTimers() {
            if (inactivityTimer) clearTimeout(inactivityTimer);
            if (absoluteTimer) clearTimeout(absoluteTimer);
            inactivityTimer = null;
            absoluteTimer = null;
        }

        function resetInactivityTimer() {
            if (inactivityTimer) clearTimeout(inactivityTimer);
            inactivityTimer = setTimeout(() => {
                xhr.abort(); // not externallyAborted — a stall, not a cancel
            }, inactivityTimeoutMs);
        }

        function onExternalAbort() {
            externallyAborted = true;
            xhr.abort();
        }
        controller.signal.addEventListener('abort', onExternalAbort, { once: true });

        function finish(fn) {
            if (settled) return;
            settled = true;
            clearTimers();
            controller.signal.removeEventListener('abort', onExternalAbort);
            fn();
        }

        xhr.upload.addEventListener('progress', (e) => {
            resetInactivityTimer();
            if (e.lengthComputable && onProgress) onProgress(e.loaded);
        });

        xhr.addEventListener('load', () => {
            finish(() => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    const eTag = xhr.getResponseHeader('ETag');
                    if (!eTag) {
                        reject(new Error(`Part ${partNumber} uploaded but returned no ETag (check bucket CORS ExposedHeaders)`));
                        return;
                    }
                    if (onProgress) onProgress(blob.size); // final tick, locks this part at exactly 100% of itself
                    resolve({ partNumber, eTag });
                } else if (xhr.status === 403) {
                    // A presigned URL rejects with 403 once expired. These
                    // URLs carry no other restrictive conditions in this
                    // app's flow, so a 403 here is treated as expiry, not
                    // a generic auth failure. Named distinctly so callers
                    // (withRetry, the upload hook) can skip pointless
                    // retries — a dead URL fails identically every time —
                    // and hide the Retry button rather than offer
                    // something that can't succeed.
                    const err = new Error(`Part ${partNumber} upload failed: presigned URL expired (403)`);
                    err.name = 'PresignedUrlExpiredError';
                    reject(err);
                } else {
                    reject(new Error(`Part ${partNumber} upload failed: ${xhr.status}`));
                }
            });
        });

        xhr.addEventListener('error', () => {
            finish(() => reject(new Error(`Part ${partNumber} upload failed: network error`)));
        });

        xhr.addEventListener('abort', () => {
            finish(() => {
                if (externallyAborted) {
                    reject(Object.assign(new Error('Aborted'), { name: 'AbortError' }));
                } else {
                    reject(new Error(`Part ${partNumber} upload stalled — no progress for ${inactivityTimeoutMs}ms`));
                }
            });
        });

        xhr.open('PUT', uploadUrl);
        resetInactivityTimer(); // starts the clock even before the first progress event
        absoluteTimer = setTimeout(() => xhr.abort(), absoluteMaxMs);
        xhr.send(blob);
    });
}

function uploadPartWithRetry(file, partNumber, uploadUrl, controller, options) {
    return withRetry(
        () => uploadPartXHR(file, partNumber, uploadUrl, controller, options),
        PART_RETRY_DELAYS_MS,
        (ms) => abortableSleep(ms, controller.signal)
    );
}

/**
 * Uploads every part of a file's multipart session, through a shared
 * concurrency pool, with per-part retry, inactivity-based stall detection,
 * and partial-resume support.
 *
 * Two independent, optional progress signals:
 * - onProgress(pct): continuous, byte-level, 0-100 across the WHOLE file.
 *   For real multi-part items (video) — never wired up for checkpoint-mode
 *   items, which deliberately only advance on discrete verified events.
 * - onPartComplete(completedCount, totalParts): discrete, fires only when
 *   a part fully lands. For checkpoint-mode items (always 1 part) — the
 *   signal that triggers the single 'uploaded' checkpoint transition.
 *
 * @returns {Promise<Record<number, string>>} completed parts (partNumber -> eTag). On failure, the thrown Error carries `.completedParts`.
 */
export async function uploadMultipart(
    file,
    { parts },
    {
        pool,
        controller,
        onProgress,
        onPartComplete,
        alreadyCompletedParts = {},
        inactivityTimeoutMs = PART_INACTIVITY_TIMEOUT_MS,
        absoluteMaxMs = PART_ABSOLUTE_MAX_MS,
    }
) {
    const completedParts = { ...alreadyCompletedParts };
    const remainingParts = parts.filter((p) => !(p.partNumber in completedParts));
    let completedCount = Object.keys(completedParts).length;

    const bytesByPart = {};
    for (const part of parts) {
        bytesByPart[part.partNumber] = part.partNumber in completedParts ? partByteLength(part.partNumber, file.size) : 0;
    }
    const reportProgress = () => {
        if (onProgress) onProgress(computeCombinedBytesPct(bytesByPart, file.size));
    };
    reportProgress(); // resumed baseline, reported immediately
    if (onPartComplete && completedCount > 0) onPartComplete(completedCount, parts.length);

    const results = await Promise.allSettled(
        remainingParts.map((part) =>
            pool
                .run(() =>
                    uploadPartWithRetry(file, part.partNumber, part.uploadUrl, controller, {
                        inactivityTimeoutMs,
                        absoluteMaxMs,
                        onProgress: (loaded) => {
                            bytesByPart[part.partNumber] = loaded;
                            reportProgress();
                        },
                    })
                )
                .then((result) => {
                    completedParts[result.partNumber] = result.eTag;
                    bytesByPart[result.partNumber] = partByteLength(result.partNumber, file.size);
                    completedCount++;
                    reportProgress();
                    if (onPartComplete) onPartComplete(completedCount, parts.length);
                    return result;
                })
        )
    );

    const firstRejection = results.find((r) => r.status === 'rejected');

    if (firstRejection) {
        const error = firstRejection.reason instanceof Error ? firstRejection.reason : new Error(String(firstRejection.reason));
        error.completedParts = completedParts;
        throw error;
    }

    return completedParts;
}